import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { RemovalRequest } from '@photos/shared';
import { useEscape } from '../hooks/useEscape';
import { fmtLongDateTime, fmtMB, fmtShortDate, wallClock } from '../lib/format';
import { VISIBILITY_LOOK } from '../lib/visibility';
import { thumbhashDataUrl } from '../lib/thumbhash';
import { useSession } from '../state/session';
import { removalReasonText } from './dialogs/RemovalDialog';
import { Icon } from './Icon';

interface RemovalReviewProps {
  requests: RemovalRequest[];
  index: number;
  onIndex(i: number): void;
  onClose(): void;
  onResolve(id: string, action: 'restore' | 'remove'): void;
  busy: boolean;
}

/** Full-size look at a photo someone asked to remove, with the request details and the decision buttons. */
export function RemovalReview({ requests, index, onIndex, onClose, onResolve, busy }: RemovalReviewProps) {
  const r = requests[index];
  const { tagIndex } = useSession();

  useEscape(onClose);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      if (e.key === 'ArrowRight' && index < requests.length - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [index, requests.length, onIndex]);

  useEffect(() => {
    if (!r) onClose();
  }, [r, onClose]);
  if (!r) return null;

  const p = r.photo;
  const title = p ? tagIndex.eventLabel(p.tags) || p.fileName : 'Photo';
  const ar = p ? p.width / p.height : 4 / 3;
  const ph = thumbhashDataUrl(p?.thumbhash);
  const look = p ? VISIBILITY_LOOK[p.visibility] : null;

  return createPortal(
    <div className="lb open" role="dialog" aria-modal="true" aria-label={`Removal request: ${title}`}>
      <div className="stage">
        <button type="button" className="x" onClick={onClose}>
          <Icon name="x" /> Close
        </button>
        <span className="pos">
          Request {index + 1} / {requests.length}
        </span>
        <button
          type="button"
          className="nav prev"
          aria-label="Previous request"
          disabled={index === 0}
          onClick={() => onIndex(index - 1)}
        >
          <Icon name="left" />
        </button>
        {p ? (
          <img
            key={p.id}
            alt={title}
            src={p.previewUrl}
            style={{
              aspectRatio: String(ar),
              width: `min(100%, calc((100vh - 112px) * ${ar}))`,
              ...(ph ? { backgroundImage: `url(${ph})`, backgroundSize: 'cover' } : {}),
            }}
          />
        ) : (
          <img alt={title} src={r.thumbUrl} />
        )}
        <button
          type="button"
          className="nav next"
          aria-label="Next request"
          disabled={index >= requests.length - 1}
          onClick={() => onIndex(index + 1)}
        >
          <Icon name="right" />
        </button>
      </div>
      <aside>
        <h3>{title}</h3>
        {p && <div className="sub">{fmtLongDateTime(wallClock(p.takenAt))}</div>}
        <div className="sec" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span className="pill" style={{ color: 'var(--danger)' }}>
            <Icon name="eye" />
            Hidden while under review
          </span>
          {p && look && (
            <span className="pill" style={{ color: look.color }}>
              <Icon name={look.icon} />
              {look.label}
            </span>
          )}
        </div>

        <div className="sec">
          <div className="lbl">Removal request</div>
          <dl className="kv">
            <dt>Reason</dt>
            <dd>{removalReasonText(r)}</dd>
            {r.note && r.reason !== 'other' && (
              <>
                <dt>Note</dt>
                <dd>{r.note}</dd>
              </>
            )}
            <dt>Requested by</dt>
            <dd>{r.requesterName}</dd>
            <dt>Date</dt>
            <dd>{fmtShortDate(new Date(r.createdAt))}</dd>
          </dl>
        </div>

        <div className="sec">
          <div className="acts">
            <button type="button" className="btn primary" disabled={busy} onClick={() => onResolve(r.id, 'restore')}>
              <Icon name="check" />
              Restore photo
            </button>
            <button type="button" className="btn danger" disabled={busy} onClick={() => onResolve(r.id, 'remove')}>
              <Icon name="trash" />
              Remove
            </button>
          </div>
          <div className="sub" style={{ marginTop: 6 }}>
            Restore puts the photo back in the library. Remove keeps it in Trash.
          </div>
        </div>

        {p && (
          <>
            <div className="sec">
              <div className="lbl">Tags</div>
              <div className="tagbox">
                {p.tags.map((t) => (
                  <span key={t} className={`chip cat-${tagIndex.catOf(t)}`}>
                    #{t}
                  </span>
                ))}
              </div>
            </div>
            <div className="sec">
              <div className="lbl">Details</div>
              <dl className="kv">
                <dt>Uploaded by</dt>
                <dd>{p.uploaderName}</dd>
                <dt>Camera</dt>
                <dd>{p.camera || '—'}</dd>
                <dt>Dimensions</dt>
                <dd>
                  {p.width} × {p.height}
                </dd>
                <dt>File</dt>
                <dd>
                  {p.fileName} · {fmtMB(p.bytes)} MB
                </dd>
              </dl>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
